import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  PaperPlaneTilt,
  ArrowLeft,
  EyeSlash,
  ShieldCheck,
  ChatCircleDots,
  MagnifyingGlass,
  Detective,
} from "@phosphor-icons/react";

export default function NetworkingAbout() {
  const { user } = useAuth();

  return (
    <div data-testid="networking-about" className="space-y-6 max-w-2xl">
      <div>
        <Link
          to="/info"
          data-testid="networking-about-back"
          className="inline-flex items-center gap-1 text-xs font-mono-time uppercase tracking-widest text-[#7A828C] hover:text-[#000000]"
        >
          <ArrowLeft size={12} /> Back to info
        </Link>
        <div className="mt-2 flex items-center gap-2">
          <PaperPlaneTilt size={20} className="text-[#000000] -rotate-[12deg]" weight="duotone" />
          <div className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest">How it works</div>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-1">Networking, explained</h1>
        <p className="text-sm text-[#5a5a5a] mt-2 leading-relaxed">
          A 90-second guide to swapping hellos with other delegates inside the app - without giving anything away you didn&apos;t mean to.
        </p>
      </div>

      <Step n="1" icon={ShieldCheck} title="It&apos;s on by default - but you&apos;re in control.">
        <p>
          When you sign in to the conference app, your name, role and
          organisation are visible to other authenticated DFTB26 delegates so
          they can say hello during the week. You can <strong>turn it off in
          one tap</strong> from your Profile if you&apos;d rather stay private -
          you&apos;ll disappear from the directory and stop receiving new DMs
          immediately.
        </p>
      </Step>

      <Step n="2" icon={Detective} title="Only what you choose to show.">
        <p>
          Other authenticated delegates see your{" "}
          <strong>name, role, organisation</strong> and the optional <strong>short bio</strong>{" "}
          you wrote on your Profile. That&apos;s it.
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1.5">
          <li>Your <strong>email is never shown</strong> to other delegates.</li>
          <li>Sessions you&apos;ve starred and questions you&apos;ve asked stay private.</li>
          <li>Your profile isn&apos;t on the public web - not on Google, not on dftb26.com.</li>
        </ul>
      </Step>

      <Step n="3" icon={MagnifyingGlass} title="Find people the way you would in real life.">
        <p>
          On the Network tab you can scroll through everyone who&apos;s visible, or
          search by <strong>name</strong>, <strong>organisation</strong> or{" "}
          <strong>role</strong> (&quot;Glasgow&quot;, &quot;SpR&quot;, &quot;consultant&quot;). Tap a card to start a
          conversation - no follow requests, no LinkedIn-style ceremony.
        </p>
      </Step>

      <Step n="4" icon={ChatCircleDots} title="Messages are private and stay in the app.">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Only you and the person you&apos;re messaging can see your chat.</li>
          <li>Organisers and admins <strong>cannot read your messages</strong>.</li>
          <li>Text only - no photos or files. If you want to share something, swap email addresses inside the chat.</li>
          <li>Conversations are kept for the conference and up to <strong>90 days after</strong>, then deleted automatically. Screenshot anything you want to keep.</li>
        </ul>
      </Step>

      <Step n="5" icon={EyeSlash} title="Change your mind, any time.">
        <p>
          Go to the <strong>Network</strong> tab and flip the <strong>&quot;Visible&quot;</strong>{" "}
          toggle off in the top-right (or use the same switch in your Profile).
          You disappear from everyone&apos;s directory instantly and can&apos;t be
          messaged by anyone new. Your existing conversations are kept (in case
          you ever flip back), but unreachable while you&apos;re off.
        </p>
        <p className="mt-2 text-xs text-[#7A828C]">
          If a delegate is bothering you, switch off and email{" "}
          <a href="mailto:hello@dftbdigital.com" className="text-[#000000] hover:underline">hello@dftbdigital.com</a>{" "}
          - we&apos;ll handle it.
        </p>
      </Step>

      <div className="p-4 bg-[#000000]/5 border border-[#000000]/20 rounded-sm">
        <p className="text-sm text-[#1c1c24] leading-relaxed">
          <strong>Worth saying out loud:</strong> the app is a nudge, not a replacement.
          The best networking at DFTB still happens over coffee, at the welcome
          reception, and on the dance floor at the conference party. The app just
          helps you remember who said the brilliant thing - and send them a note
          afterwards.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        {user ? (
          <Button asChild className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] h-12 px-6 font-semibold flex-1 sm:flex-none" data-testid="networking-about-go">
            <Link to="/networking">
              {user.networking_opt_in ? "Go to Networking" : "Go to Networking"}
            </Link>
          </Button>
        ) : (
          <Button asChild className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] h-12 px-6 font-semibold flex-1 sm:flex-none" data-testid="networking-about-signin">
            <Link to="/login">Sign in</Link>
          </Button>
        )}
        <Button asChild variant="outline" className="rounded-sm h-12 px-6 flex-1 sm:flex-none" data-testid="networking-about-privacy">
          <Link to="/privacy">Read the full privacy policy</Link>
        </Button>
      </div>
    </div>
  );
}

function Step({ n, icon: Icon, title, children }) {
  return (
    <section className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-sm bg-[#000000]/10 grid place-items-center">
          <Icon size={18} className="text-[#000000]" weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest">Step {n}</span>
          </div>
          <h2 className="font-display text-lg mt-0.5">{title}</h2>
          <div className="text-sm text-[#5a5a5a] leading-relaxed mt-2">{children}</div>
        </div>
      </div>
    </section>
  );
}
